// Home → Reports (spec 044 D76–D89, plan slice 8): the Figma frame "Dashboard · Home"
// (11501:95780) — the head, four cards with their trend lines, Lessons over time by subject
// (D126–D129) beside the Session status ring, and the two audience cards. The reads are
// src/services/reports.js; the arithmetic is src/utils/reportNumbers.js (live numbers) and
// src/utils/reportCharts.js (the hourly summaries).
import React, { useEffect, useState } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { RiDownload2Line } from "@remixicon/react";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { getReport, getReportStats } from "../../services/reports";
import { ListHead } from "../../components/kit/PageHead";
import SegmentTabs from "../../components/kit/SegmentTabs";
import KpiCard from "../../components/kit/KpiCard";
import { Donut } from "../../components/kit/Charts";
import { ErrorState } from "../../components/kit/States";
import { headButtonSx, tokens } from "../../utils/figmaTokens";
import { countText } from "../../utils/kitPresentation";
import { DEFAULT_PERIOD, PERIODS, greeting, periodWords, reportWindows } from "../../utils/reportPeriods";
import { RING_ROWS, reportCards, reportCsv, reportNumbers, ringParts } from "../../utils/reportNumbers";
import { chartSeries, subjectChart, subjectSpan, updatedLine } from "../../utils/reportCharts";
import { SubjectPanel, UsersByCountry, UsersByGender } from "./Panels";
import BusiestHours from "./BusiestHours";

// What the page knows before the numbers land, or when the test accounts could not be read.
const unknown = (windows) => reportNumbers({ internal: null }, windows);
const wait = (width) => <Skeleton width={width} />;
// Which series of the summaries each card's trend line draws (D85).
const TREND = { revenue: "revenue", students: "newStudents", sessions: "sessions", teachers: "activeTeachers" };

const Reports = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [attempt, setAttempt] = useState(0);
  const [report, setReport] = useState(null); // { windows, numbers } for `period`, once read
  const [stats, setStats] = useState(null); // { windows, days, audience, hours } or { windows, failed }

  useEffect(() => {
    let live = true;
    const windows = reportWindows(period, new Date());
    setReport(null);
    setStats(null);
    getReport(windows)
      .then((raw) => reportNumbers(raw, windows))
      .catch((e) => {
        console.error("Reports: the report failed:", e);
        return unknown(windows);
      })
      .then((numbers) => {
        if (live) setReport({ windows, numbers });
      });
    getReportStats(windows, subjectSpan(windows.now))
      .then((s) => ({ windows, ...s }))
      .catch((e) => {
        console.error("Reports: the summaries failed:", e);
        return { windows, failed: true };
      })
      .then((s) => {
        if (live) setStats(s);
      });
    return () => {
      live = false;
    };
  }, [period, attempt]);

  const loading = report === null;
  const windows = report ? report.windows : reportWindows(period, new Date());
  const numbers = report ? report.numbers : unknown(windows);
  const cards = reportCards(numbers, windows);
  const ring = ringParts(numbers.sessions.cur);
  // D85: the charts need the job to have run once — stats/audience is its mark.
  const chartState = !stats ? "loading" : stats.failed ? "failed" : stats.audience ? "ready" : "empty";
  const series = chartState === "ready" ? chartSeries(stats.days, stats.windows) : null;
  // D126: the subject chart also needs a full run since D126 — stats/audience.bySubject marks it.
  const subjectState = chartState === "ready" && !stats.audience.bySubject ? "empty" : chartState;
  const subjects = subjectState === "ready" ? subjectChart(stats.days, stats.windows.now) : null;
  const retry = () => setAttempt((n) => n + 1);

  // R12: the same download Transactions makes.
  const doExport = () => {
    const { text, filename } = reportCsv(numbers, windows);
    const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
        <ListHead caption="Home" title="Reports" subtitle={greeting(user?.name || user?.displayName, period)} />
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
          <SegmentTabs label="Period" tabs={PERIODS} value={period} onChange={setPeriod} />
          <Button onClick={doExport} disabled={loading} disableRipple disableFocusRipple sx={headButtonSx}>
            <RiDownload2Line size={18} />
            Export
          </Button>
        </Box>
      </Box>

      {!loading && numbers.failed && (
        <ErrorState
          compact
          title="Some numbers could not load"
          message="Numbers that could not be read show —. Try again in a moment."
          onRetry={retry}
        />
      )}

      <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" } }}>
        {cards.map((c) => (
          <KpiCard
            key={c.key}
            label={c.label}
            value={loading ? wait(96) : c.value}
            chip={loading ? null : c.chip}
            caption={loading ? wait(140) : c.caption}
            trend={loading || !series ? null : series[TREND[c.key]]}
          />
        ))}
      </Box>

      <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 420px" } }}>
        <SubjectPanel
          state={subjectState}
          chart={subjects}
          updated={chartState === "ready" ? updatedLine(stats.audience.builtAt, windows.now) : null}
          onRetry={retry}
        />
        <Box
          component="section"
          aria-labelledby="reports-ring-title"
          sx={{
            bgcolor: tokens.surface.default,
            border: `1px solid ${tokens.border.default}`,
            borderRadius: "16px",
            p: { xs: "16px", sm: "32px" },
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            minWidth: 0,
            maxWidth: 420,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Typography
              id="reports-ring-title"
              component="h2"
              sx={{ fontSize: 21, lineHeight: "28px", fontWeight: 600, letterSpacing: "-0.2px", color: tokens.text.primary }}
            >
              Session status
            </Typography>
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
              All sessions booked for {periodWords(period, windows.now)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {loading ? (
              <Skeleton variant="circular" width={240} height={240} />
            ) : (
              <Donut segments={ring ? ring.segments : []} label="Booked" value={ring ? countText(ring.total) : "—"} />
            )}
          </Box>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
            {RING_ROWS.map((r) => (
              <Box component="li" key={r.key} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: tokens.chart[r.tone], flexShrink: 0 }} />
                <Typography variant="bodyMedium" sx={{ flex: 1, minWidth: 0, color: tokens.text.secondary }}>
                  {r.label}
                </Typography>
                <Typography component="span" variant="bodyMediumStrong" sx={{ color: tokens.text.primary }}>
                  {loading ? wait(24) : ring ? countText(numbers.sessions.cur[r.key]) : "—"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 420px" }, alignItems: "start" }}>
        <UsersByCountry state={chartState} audience={stats && stats.audience} onRetry={retry} />
        <UsersByGender state={chartState} audience={stats && stats.audience} onRetry={retry} />
      </Box>

      <BusiestHours
        state={chartState}
        hours={stats && stats.hours}
        showSchedule={hasPermission(user, permissions.VIEW_LESSONS)}
        onRetry={retry}
      />
    </Box>
  );
};

export default Reports;
